import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { alertTarget } from '../../utils/alertLink';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BellIcon = () => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 15.5v-5a6 6 0 10-12 0v5L4.5 18h15z" />
        <path d="M10 20.5a2 2 0 004 0" />
    </svg>
);

const timeAgo = (dateStr) => {
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

const NotificationBell = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const wrapRef = useRef(null);
    const { user } = useAuth();
    const openRef = useRef(false);

    const fetchUnreadCount = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API}/api/user-alerts/unread-count`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUnreadCount(res.data.count || 0);
        } catch (err) { }
    };

    const fetchAlerts = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API}/api/user-alerts`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAlerts(res.data.alerts || []);
        } catch (err) { }
    };

    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000); // poll every 30s
        return () => clearInterval(interval);
    }, []);

    useEffect(() => { openRef.current = open; }, [open]);

    // Live alerts: the server pushes `newAlert` the moment one is created.
    useEffect(() => {
        if (!user?.id) return undefined;
        // the socket library (about 30 KB) is only needed for signed-in members, so it is downloaded here and not with every page
        let socket = null;
        let cancelled = false;
        import('socket.io-client').then(({ io }) => {
            if (cancelled) return;
            socket = io(API, { auth: (cb) => cb({ token: localStorage.getItem('token') }) }); // the server checks this token
            socket.on('newAlert', () => {
                fetchUnreadCount();
                if (openRef.current) fetchAlerts();
            });
        });
        return () => { cancelled = true; if (socket) socket.disconnect(); };
    }, [user?.id]);

    // tablet and phone (menu button layout): the list opens as a pop-up window instead of a dropdown
    const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches);
    useEffect(() => {
        const query = window.matchMedia('(max-width: 1024px)');
        const update = () => setCompact(query.matches);
        update();
        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, []);

    useEffect(() => {
        if (compact) return undefined; // the pop-up closes from its own backdrop and close button
        const handleClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [compact]);

    useEffect(() => {
        if (!open || !compact) return undefined;
        const onKey = event => { if (event.key === 'Escape') setOpen(false); };
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = previous;
            window.removeEventListener('keydown', onKey);
        };
    }, [open, compact]);

    const toggleOpen = () => {
        const next = !open;
        setOpen(next);
        if (next) fetchAlerts();
    };

    const handleAlertClick = async (alert) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/api/user-alerts/${alert._id}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAlerts(prev => prev.map(a => a._id === alert._id ? { ...a, isRead: true } : a));
            setUnreadCount(prev => Math.max(0, prev - (alert.isRead ? 0 : 1)));
        } catch (err) { }
        setOpen(false);
        const target = alertTarget(alert);
        if (target) navigate(target);
    };

    const markAllRead = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/api/user-alerts/mark-all-read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAlerts(prev => prev.map(a => ({ ...a, isRead: true })));
            setUnreadCount(0);
        } catch (err) { }
    };

    const panel = (
        <>
            <div style={styles.dropHeader}>
                <span style={styles.dropTitle}>Notifications</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
                    {unreadCount > 0 && (
                        <span style={styles.markAll} onClick={markAllRead}>Mark all read</span>
                    )}
                    {compact && (
                        <button type="button" style={styles.closeBtn} onClick={() => setOpen(false)} aria-label="Close notifications">
                            <X size={18} />
                        </button>
                    )}
                </span>
            </div>
            <div style={compact ? styles.modalList : styles.dropList}>
                {alerts.length === 0 ? (
                    <div style={styles.empty}>No notifications yet</div>
                ) : (
                    alerts.map(a => (
                        <div key={a._id}
                            style={{ ...styles.alertItem, background: a.isRead ? 'transparent' : 'color-mix(in srgb, var(--primary) 8%, transparent)' }}
                            onClick={() => handleAlertClick(a)}>
                            {!a.isRead && <span style={styles.dot} />}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={styles.alertTitle}>{a.title}</div>
                                <div style={styles.alertMsg}>{a.message}</div>
                                {a.attachments?.length > 0 && (
                                    <div style={styles.alertAttach}>{a.attachments.length} attachment{a.attachments.length > 1 ? 's' : ''}</div>
                                )}
                                <div style={styles.alertTime}>{timeAgo(a.createdAt)}</div>
                            </div>
                        </div>
                    ))
                )}
            </div>
            <div style={styles.viewAll} onClick={() => { setOpen(false); navigate('/notifications'); }}>View all notifications</div>
        </>
    );

    return (
        <div ref={wrapRef} style={{ position: 'relative' }}>
            <button onClick={toggleOpen} style={styles.bellBtn} title="Notifications">
                <BellIcon />
                {unreadCount > 0 && (
                    <span style={styles.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
            </button>

            {/* Desktop: a small dropdown under the bell. Tablet and phone: a centred pop-up window (the menu panel would cut a dropdown off). */}
            {open && !compact && <div style={styles.dropdown}>{panel}</div>}
            {open && compact && createPortal(
                <div style={styles.modalBackdrop} onClick={() => setOpen(false)}>
                    <div style={styles.modalCard} role="dialog" aria-modal="true" aria-label="Notifications" onClick={event => event.stopPropagation()}>
                        {panel}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

const styles = {
    bellBtn: {
        position: 'relative', width: '44px', height: '44px', borderRadius: '12px',
        background: '#ffffff', border: '1px solid rgba(226, 232, 240, 0.95)',
        color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)', transition: 'all 0.2s ease',
    },
    badge: {
        position: 'absolute', top: '-4px', right: '-4px',
        background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))', color: '#fff', fontSize: '10px', fontWeight: '800',
        minWidth: '18px', height: '18px', borderRadius: '999px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 4px', border: '2px solid #ffffff', boxShadow: '0 2px 4px color-mix(in srgb, var(--primary-dark) 25%, transparent)',
    },
    dropdown: {
        position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: '340px',
        background: 'rgba(255, 255, 255, 0.98)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(226, 232, 240, 0.95)', borderRadius: '16px',
        boxShadow: '0 20px 48px -10px rgba(15, 23, 42, 0.12), 0 4px 12px rgba(15, 23, 42, 0.04)', overflow: 'hidden', zIndex: 1200,
    },
    modalBackdrop: {
        position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px', background: 'rgba(20, 8, 12, 0.5)',
    },
    modalCard: {
        width: 'min(520px, 100%)', maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        background: '#ffffff', borderRadius: '22px', overflow: 'hidden',
        boxShadow: '0 30px 80px -12px rgba(20, 8, 12, 0.5)',
    },
    modalList: { flex: 1, minHeight: 0, overflowY: 'auto' },
    closeBtn: {
        width: '34px', height: '34px', display: 'grid', placeItems: 'center', border: '1px solid rgba(226, 232, 240, 0.95)',
        borderRadius: '50%', background: '#ffffff', color: '#475569', cursor: 'pointer', padding: 0,
    },
    dropHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 18px', borderBottom: '1px solid rgba(226, 232, 240, 0.9)',
        background: '#f8fafc',
    },
    dropTitle: { fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: '700', color: '#0f172a' },
    markAll: { fontSize: '12px', fontWeight: '700', color: 'var(--primary)', cursor: 'pointer' },
    dropList: { maxHeight: '380px', overflowY: 'auto' },
    empty: { padding: '36px 16px', textAlign: 'center', fontSize: '13.5px', color: 'var(--text-muted)' },
    alertItem: {
        display: 'flex', gap: '10px', padding: '12px 18px', cursor: 'pointer',
        borderBottom: '1px solid color-mix(in srgb, var(--c-b4737e, #b4737e) 10%, transparent)', transition: 'background 0.15s ease',
    },
    dot: { width: '8px', height: '8px', borderRadius: '50%', background: 'var(--primary)', flexShrink: 0, marginTop: '6px' },
    alertTitle: { fontSize: '13.5px', fontWeight: '700', color: 'var(--text)' },
    alertMsg: { fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '3px', lineHeight: 1.45 },
    alertAttach: { fontSize: '11.5px', color: 'var(--primary)', marginTop: '4px', fontWeight: '600' },
    viewAll: { padding: '12px 18px', textAlign: 'center', fontSize: '12.5px', fontWeight: '700', color: 'var(--primary)', cursor: 'pointer', borderTop: '1px solid color-mix(in srgb, var(--c-b4737e, #b4737e) 16%, transparent)', background: 'color-mix(in srgb, var(--c-fbf8f4, #fbf8f4) 50%, transparent)' },
    alertTime: { fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' },
};

export default NotificationBell;
